import { ReactComponent as QuickQuincyIcon } from "shared/assets/images/quick-quincy-start-icon.svg";
import { ReactComponent as QuincyChatIcon } from "shared/assets/images/quincy-chat-icon.svg";
import { ReactComponent as BoltIcon } from "shared/assets/images/icon-bolt.svg";
import {
  LoadingOutlined,
  UpOutlined,
  DownOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import Markdown from "markdown-to-jsx";
import { Col, Input, Row, Spin, Tag } from "antd";
import {
  ArtistState,
  getLoggedUserArtistQuincySuggestions,
} from "explorer/reducers/artistas";
import { useState, useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { generateGoalsQuincy } from "shared/service/StrmService";
import "./QuickQuincy.scss";
import useSubscription, {
  SubscriptionPlan,
} from "shared/hooks/useSubscription";
import { SubscriptionState } from "explorer/reducers/subscription";

const QuickQuincyGoals: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { currentPlan } = useSubscription();

  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const { quincyUsage } = useSelector(
    (state: SubscriptionState) => state.subscription
  );

  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [suggestionQuincy, setSuggestionQuincy] = useState<string>("");

  const disableGenerateButton = useMemo(() => {
    return (
      loading || (currentPlan === SubscriptionPlan.START && quincyUsage <= 0)
    );
  }, [loading, currentPlan, quincyUsage]);

  useEffect(() => {
    if (userArtist?.quincyMilestoneSuggestions) {
      setSuggestionQuincy(userArtist.quincyMilestoneSuggestions);
    }
  }, [userArtist?.quincyMilestoneSuggestions]);

  const onGenerateSuggestion = async () => {
    setLoading(true);
    try {
      const suggestion = await generateGoalsQuincy(
        userArtist.id!,
        i18n.language
      );

      if (typeof suggestion === "string") {
        setSuggestionQuincy(suggestion);
      } else {
        console.error("suggestion response:", suggestion);
      }
      dispatch(getLoggedUserArtistQuincySuggestions());
    } catch (error) {
      console.error("Error generating suggestion:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quick-quincy">
      <Row className="header">
        <span className="title">
          <QuickQuincyIcon /> Quincy
        </span>

        {currentPlan === SubscriptionPlan.START && (
          <Tag className="tag-credits">
            <BoltIcon fill="#f9b3de" />
            {quincyUsage ? `Credits: ${quincyUsage}` : "Back in 24h"}
          </Tag>
        )}
      </Row>
      {loading ? (
        <Row className="body">
          <p className="linha-loading">
            <Spin indicator={<LoadingOutlined />} />
            {t("components.quinckQuincy.Goals.generating")}
          </p>
        </Row>
      ) : (
        <>
          <Row className="body">
            {suggestionQuincy ? (
              <div>
                <div
                  className={`markdown-text ${collapsed ? "collapsed" : ""}`}
                >
                  <Markdown style={{ lineHeight: "140%" }}>
                    {suggestionQuincy}
                  </Markdown>
                </div>
                <div>
                  <button
                    onClick={() => setCollapsed((prev) => !prev)}
                    className="botao-ver-mais"
                  >
                    {collapsed ? (
                      <>
                        {t("components.quinckQuincy.Goals.seeMore")}{" "}
                        <DownOutlined />
                      </>
                    ) : (
                      <>
                        {t("components.quinckQuincy.Goals.seeLess")}{" "}
                        <UpOutlined />
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <p>
                <Trans
                  i18nKey="components.quinckQuincy.Goals.intro"
                  values={{ userArtistName: userArtist?.name }}
                />
              </p>
            )}
          </Row>

          <Row className="footer">
            <Col xs={24}>
              <div className="botoes-regerar">
                <button
                  className="botao-gerar-quincy botao-vazado"
                  onClick={onGenerateSuggestion}
                  disabled={disableGenerateButton}
                  style={{
                    cursor: disableGenerateButton ? "not-allowed" : "pointer",
                  }}
                >
                  <QuickQuincyIcon />
                  {suggestionQuincy
                    ? t("components.quinckQuincy.Goals.update")
                    : t("components.quinckQuincy.Goals.start")}
                </button>

                {suggestionQuincy && (
                  <button
                    className="botao-gerar-quincy botao-vazado"
                    onClick={() => history.push("/quincy")}
                  >
                    <QuincyChatIcon />
                    {t("components.quinckQuincy.Goals.newChat")}
                  </button>
                )}
              </div>
            </Col>
            {suggestionQuincy && (
              <Col xs={24}>
                <div className="prompt-explain">
                  <Input
                    className="input-prompt-quincy"
                    value={String(
                      t("components.quinckQuincy.Goals.promptMessage")
                    )}
                    readOnly
                    suffix={
                      <span
                        className="icon-arrow-right"
                        onClick={() =>
                          history.push("/quincy", {
                            firstMessage: `${suggestionQuincy} \n\n ${t(
                              "components.quinckQuincy.Goals.promptMessage"
                            )}`,
                          })
                        }
                      >
                        <ArrowRightOutlined />
                      </span>
                    }
                  />
                </div>
              </Col>
            )}
          </Row>
        </>
      )}
    </div>
  );
};

export default QuickQuincyGoals;
